/**
 * @file PlanDetailsView.js
 * @description Detail view for a single benefit plan.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styles from './PlanDetailsView.module.scss';
import Spinner from '../ui/spinners/Spinner';
import Toast from '../ui/toasts/Toast';
import {
    formatPhoneNumber,
    hasText,
    isValidUrl,
    readBoolOption,
} from '../../utils/utils';
import formatCurrencyString, {
    formatCurrencyStringV2,
} from '../../utils/formatCurrency';
import { ROUTES } from '../../constants/routes';
import {
    ClientOptionEnum,
    EventDataTypeEnum,
    LogEventType,
} from '../../utils/data/enums';
import { useAuthContext } from '../../store/Contexts';
import {
    DISPLAY_MODE,
    resolveDisplayMode,
} from '../plans/_helpers/planTypeDisplayMode';
import {
    fetchBenefitPlanById,
    fetchPlanType,
    searchPlanTypes,
} from '../../services/benefitPlanService';
import { fetchPlanYearById } from '../../services/planYearService';
import { fetchAssetByID, getClientOptions } from '../../services/clientService';
import { getIndividualEnrollment } from '../../services/individualService';
import { fetchMemberOf } from '../../services/populationGroupService';
import {
    fetchClientSSOById,
    fetchSystemSSOById,
    loginClientSSO,
} from '../../services/ssoService';
import { logEvent } from '../../services/loggingService';
import {
    showSSOGenericError,
    showSSOLoginValidationWarning,
} from '../../utils/ssoLinkClickHandler';
import {
    closePendingWindow,
    navigatePendingWindow,
    openPendingWindow,
} from '../../utils/pendingWindow';
import RichTextContent from '../ui/RichTextContent';
import PlanDetailHero from './_components/PlanDetailHero';
import PlanOverviewGrid from './_components/PlanOverviewGrid';
import AssetsRow from './_components/AssetsRow';
import ContactBlock from './_components/ContactBlock';
import { filterVisibleEnrollments } from './benefitVisibility';
import { getText } from '../../i18n';
import {
    parseLocalDate as parseDate,
    formatCoverageDate,
} from './_helpers/dateHelpers';

const MED_DEN_VIS_PLAN_TYPES = [1, 2, 3];
const HR_CONTACT_CATEGORY_ID = 22;
const INFINITE_END = '9999-12-31';
const LOCALE = 'en-US';
const CURRENCY = 'USD';

function ensureUrl(value) {
    if (!value) {
        return '';
    }
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

function formatMoney(value) {
    if (value == null || value === '') {
        return null;
    }
    return formatCurrencyString(value, {
        locale: LOCALE,
        currency: CURRENCY,
        emptyDisplay: '-',
    });
}

function formatOverviewValue(value) {
    if (value == null) {
        return '';
    }

    const normalizedValue =
        typeof value === 'string' ? value.trim() : String(value).trim();

    return normalizedValue;
}

function buildOverviewItem(label, value, options = {}) {
    const displayValue = formatOverviewValue(value);
    if (!displayValue) {
        return null;
    }

    const href = options.href ? ensureUrl(options.href) : '';
    const hasLink = options.kind === 'link' && href;
    const hasButton = options.kind === 'button' && options.onClick;

    if (options.kind === 'link' && !hasLink) {
        return null;
    }

    if (options.kind === 'button' && !hasButton) {
        return null;
    }

    if (!options.kind) {
        return {
            label,
            value: displayValue,
        };
    }

    return {
        label,
        value: displayValue,
        kind: options.kind,
        href: hasLink ? href : undefined,
        onClick: options.onClick,
        ariaLabel: options.ariaLabel,
    };
}

export function buildEnrollmentOverviewItems({
    enrollment,
    displayEmployeeCosts,
    displayEmployerCosts,
    formatMoney: moneyFormatter,
    getText: textGetter,
}) {
    const items = [];
    const pushItem = (label, value) => {
        const item = buildOverviewItem(label, value);
        if (item) {
            items.push(item);
        }
    };

    if (displayEmployeeCosts) {
        pushItem('Your Cost', moneyFormatter(enrollment?.employeeCost));
    }

    if (displayEmployerCosts) {
        pushItem('Employer Cost', moneyFormatter(enrollment?.employerCost));
    }

    pushItem(textGetter('msg_5bd44ebe63cd'), enrollment?.tier);
    pushItem(
        textGetter('msg_80e13549b47a'),
        formatCurrencyStringV2(enrollment?.volume, {
            locale: LOCALE,
            currency: CURRENCY,
            emptyDisplay: null,
        }),
    );
    pushItem(textGetter('msg_3f18c98c7e88'), enrollment?.memo);

    return items;
}

function buildGroupedDetails(planData, planYearId) {
    if (!planData?.planDetails || !planData?.planTypeDetailCategories) {
        return [];
    }

    const detailsForYear = planData.planDetails.filter(function forYear(d) {
        return String(d.planYearId) === String(planYearId);
    });

    const configByCategory = {};
    planData.planTypeDetailCategories.forEach(function indexCategory(cat) {
        const map = {};
        (cat.planTypeDetails ?? []).forEach(function indexDetail(td) {
            map[td.systemPlanDetailId] = {
                sortOrder: td.sortOrder,
                isActive: td.isActive,
                name: td.name,
            };
        });
        configByCategory[cat.systemPlanDetailCategoryId] = map;
    });

    return planData.planTypeDetailCategories
        .filter(function activeCategory(cat) {
            return cat.isActive;
        })
        .sort(function bySortOrder(a, b) {
            return a.sortOrder - b.sortOrder;
        })
        .map(function buildGroup(cat) {
            const categoryId = cat.systemPlanDetailCategoryId;
            const configs = configByCategory[categoryId] ?? {};
            const details = detailsForYear
                .filter(function inCategory(d) {
                    const matches =
                        d.systemPlanDetail?.systemPlanDetailCategoryId ===
                        categoryId;
                    const cfg = configs[d.systemPlanDetail?.id];
                    return matches && cfg?.isActive !== false;
                })
                .sort(function bySortOrder(a, b) {
                    const sa =
                        configs[a.systemPlanDetail?.id]?.sortOrder ??
                        Number.MAX_SAFE_INTEGER;
                    const sb =
                        configs[b.systemPlanDetail?.id]?.sortOrder ??
                        Number.MAX_SAFE_INTEGER;
                    return sa - sb;
                })
                .map(function applyNameOverride(d) {
                    const overrideName = configs[d.systemPlanDetail?.id]?.name;
                    return overrideName
                        ? { ...d, name: d.name || overrideName }
                        : d;
                });
            const categoryName =
                cat.name ||
                details[0]?.systemPlanDetail?.systemPlanDetailCategory?.name ||
                cat.systemPlanDetailCategory?.name ||
                getText('msg_a3c686e711e4');
            return { categoryId, categoryName, details };
        })
        .filter(function nonEmpty(group) {
            return group.details.length > 0;
        });
}

function findCategoryText(planData, planYearId, categoryId) {
    if (!planData?.planDetailCategories) {
        return '';
    }
    const found = planData.planDetailCategories.find(function match(c) {
        return (
            String(c.planYearId) === String(planYearId) &&
            c.systemPlanDetailCategoryId === categoryId
        );
    });
    return found?.text ?? '';
}

function findEnrollment(enrollments, planId, planYear) {
    if (!enrollments?.length || !planYear) {
        return null;
    }
    const yearStart = parseDate(planYear.startDate);
    const yearEnd = parseDate(planYear.endDate);
    const infinite = parseDate(INFINITE_END).getTime();
    const numericPlanId = Number(planId);

    return enrollments.find(function matches(enrollment) {
        if (enrollment.planId !== numericPlanId) {
            return false;
        }
        const eStart = parseDate(enrollment.startDate);
        const eEnd = parseDate(enrollment.endDate);
        const startsBeforeEnd = eStart <= yearEnd;
        const endsAfterStart = eEnd >= yearStart || eEnd.getTime() === infinite;
        return startsBeforeEnd && endsAfterStart;
    });
}

function parsePlanRouteParam(rawPlanId) {
    if (!rawPlanId) {
        return { planId: null, clientPlanTypeId: null };
    }

    if (/^pt-\d+$/i.test(rawPlanId)) {
        return {
            planId: null,
            clientPlanTypeId: Number(rawPlanId.slice(3)),
        };
    }

    const numericPlanId = Number(rawPlanId);
    if (Number.isNaN(numericPlanId)) {
        return { planId: null, clientPlanTypeId: null };
    }

    return { planId: numericPlanId, clientPlanTypeId: null };
}

function toSyntheticPlanFromPlanType(planTypeRecord) {
    const planTypeName = planTypeRecord?.name || planTypeRecord?.planType?.name;

    return {
        id: null,
        name: planTypeName || getText('msg_ae2f98a099fe'),
        planTypeId:
            planTypeRecord?.planTypeId ?? planTypeRecord?.planType?.id ?? null,
        planType:
            planTypeRecord?.planType ??
            (planTypeName ? { name: planTypeName } : null),
        imageUrl: planTypeRecord?.imageUrl ?? null,
        networkType: null,
        networkName: '',
        policy: '',
        providerList: '',
        vendor: null,
        url: '',
        tenantSSOId: null,
        phoneNumber: '',
        headerDisplay: '',
        planAssets: [],
        planDetails: [],
        planTypeDetailCategories: [],
        planDetailCategories: [],
    };
}

function buildMemberGroupIdSet(memberOf = []) {
    return new Set(
        memberOf
            .map(function toId(group) {
                return group?.id;
            })
            .filter(function isDefined(groupId) {
                return groupId != null;
            }),
    );
}

function resolvePopulationGroupIds(planType) {
    if (Array.isArray(planType?.populationGroupIds)) {
        return planType.populationGroupIds;
    }

    if (!Array.isArray(planType?.populationGroups)) {
        return [];
    }

    return planType.populationGroups
        .map(function toId(group) {
            return group?.id;
        })
        .filter(function isDefined(groupId) {
            return groupId != null;
        });
}

function canAccessPlanType(planType, memberOf = []) {
    if (!planType) {
        return true;
    }

    const displayMode = resolveDisplayMode(planType);
    if (displayMode === DISPLAY_MODE.DO_NOT_DISPLAY) {
        return false;
    }

    if (displayMode !== DISPLAY_MODE.POPULATION) {
        return true;
    }

    const memberGroupIds = buildMemberGroupIdSet(memberOf);
    return resolvePopulationGroupIds(planType).some(function hasMatch(groupId) {
        return memberGroupIds.has(groupId);
    });
}

export default function PlanDetailsView() {
    const navigate = useNavigate();
    const location = useLocation();
    const { planId: rawPlanId, planYearId } = useParams();
    const { user } = useAuthContext();
    const userId = user?.jwt?.ind;
    const { planId, clientPlanTypeId } = useMemo(
        function resolveRouteTarget() {
            return parsePlanRouteParam(rawPlanId);
        },
        [rawPlanId],
    );
    const isFromViewBenefits = location.state?.from === ROUTES.VIEW_BENEFITS;
    const shouldLoadIndividualContext = Boolean(userId) && !isFromViewBenefits;

    const [isLoading, setIsLoading] = useState(true);
    const [planData, setPlanData] = useState(null);
    const [planYearData, setPlanYearData] = useState(null);
    const [enrollments, setEnrollments] = useState([]);
    const [assetDetailsMap, setAssetDetailsMap] = useState({});
    const [displayOutOfNetwork, setDisplayOutOfNetwork] = useState(true);
    const [displayEmployeeCosts, setDisplayEmployeeCosts] = useState(true);
    const [displayEmployerCosts, setDisplayEmployerCosts] = useState(true);
    const [planTypes, setPlanTypes] = useState([]);
    const [memberOf, setMemberOf] = useState([]);
    const [ssoUrl, setSsoUrl] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 786);
    const [planDetailCategoryContent, setPlanDetailCategoryContent] =
        useState('');
    const planLogId = Number(planId) || null;
    const benefitPlanContextName = useMemo(
        function resolveBenefitPlanContextName() {
            const planTypeName =
                planData?.planType?.name ||
                planData?.planTypeName ||
                getText('msg_ae2f98a099fe');
            const planName = planData?.name || getText('msg_82a78c8f567d');
            const planYearName =
                planYearData?.name || getText('msg_a6b7c8d9e0f1');

            return `${planTypeName} - ${planName} ${planYearName}`;
        },
        [planData, planYearData],
    );

    const handleClose = useCallback(
        function close() {
            const fallbackRoute = ROUTES.MY_BENEFITS;
            navigate(location.state?.from || fallbackRoute);
        },
        [location.state, navigate],
    );

    useEffect(function setupResize() {
        function handleResize() {
            setIsMobile(window.innerWidth <= 786);
        }
        window.addEventListener('resize', handleResize);
        return function cleanup() {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(
        function syncCategoryContent() {
            if (planData?.planDetailCategories && planYearId != null) {
                const found = planData.planDetailCategories.find(
                    function match(item) {
                        return (
                            String(item.planYearId) === String(planYearId) &&
                            item.systemPlanDetailCategoryId ===
                                HR_CONTACT_CATEGORY_ID
                        );
                    },
                );
                setPlanDetailCategoryContent(found?.text || '');
            } else {
                setPlanDetailCategoryContent('');
            }
        },
        [planData, planYearId],
    );

    useEffect(
        function loadAll() {
            if ((!planId && !clientPlanTypeId) || !planYearId) {
                setIsLoading(false);
                return undefined;
            }
            let cancelled = false;
            async function load() {
                setIsLoading(true);
                try {
                    const planRequest = planId
                        ? fetchBenefitPlanById(planId)
                        : fetchPlanType(clientPlanTypeId, planYearId).then(
                              function mapPlanTypeResponse(planTypeRecord) {
                                  return toSyntheticPlanFromPlanType(
                                      planTypeRecord,
                                  );
                              },
                          );

                    const [
                        plan,
                        planYear,
                        enrollmentList,
                        optionsRes,
                        memberOfRes,
                        planTypeRows,
                    ] = await Promise.all([
                        planRequest,
                        fetchPlanYearById(planYearId, true),
                        shouldLoadIndividualContext
                            ? getIndividualEnrollment(userId, {})
                            : Promise.resolve([]),
                        getClientOptions(0, [
                            ClientOptionEnum.DisplayOutOfNetworkInformation,
                            ClientOptionEnum.DisplayEmployeeCosts,
                            ClientOptionEnum.DisplayEmployerCosts,
                        ]),
                        shouldLoadIndividualContext
                            ? fetchMemberOf(userId)
                            : Promise.resolve([]),
                        searchPlanTypes(null, planYearId),
                    ]);
                    if (cancelled) {
                        return;
                    }

                    setPlanData(plan);
                    setPlanYearData(planYear);
                    setEnrollments(enrollmentList);
                    setMemberOf(memberOfRes);
                    setPlanTypes(planTypeRows);
                    setDisplayOutOfNetwork(
                        readBoolOption(
                            optionsRes,
                            ClientOptionEnum.DisplayOutOfNetworkInformation,
                        ),
                    );
                    setDisplayEmployeeCosts(
                        readBoolOption(
                            optionsRes,
                            ClientOptionEnum.DisplayEmployeeCosts,
                        ),
                    );
                    setDisplayEmployerCosts(
                        readBoolOption(
                            optionsRes,
                            ClientOptionEnum.DisplayEmployerCosts,
                        ),
                    );

                    const assets = plan?.planAssets ?? [];
                    if (assets.length > 0) {
                        const fetched = await Promise.all(
                            assets.map(function loadAsset(asset) {
                                return fetchAssetByID(asset.assetId).catch(
                                    function onError() {
                                        return null;
                                    },
                                );
                            }),
                        );
                        if (cancelled) {
                            return;
                        }
                        const map = {};
                        fetched.forEach(function index(detail) {
                            if (detail?.id) {
                                map[detail.id] = detail;
                            }
                        });
                        setAssetDetailsMap(map);
                    }

                    if (plan?.tenantSSOId != null) {
                        let ssoData = await fetchClientSSOById(
                            plan.tenantSSOId,
                        );
                        if (!hasText(ssoData?.url) && ssoData?.ssoId != null) {
                            ssoData = await fetchSystemSSOById(ssoData.ssoId);
                        }
                        if (!cancelled) {
                            setSsoUrl(ssoData?.url ?? '');
                        }
                    }
                } catch (err) {
                    console.error('Error retrieving plan details', err);
                    Toast.error(getText('msg_991ef6bca9fe'));
                } finally {
                    if (!cancelled) {
                        setIsLoading(false);
                    }
                }
            }
            load();
            return function cleanup() {
                cancelled = true;
            };
        },
        [
            clientPlanTypeId,
            planId,
            planYearId,
            shouldLoadIndividualContext,
            userId,
        ],
    );

    const enrollment = useMemo(
        function resolveEnrollment() {
            const visibleEnrollments = filterVisibleEnrollments(
                enrollments,
                planTypes,
                memberOf,
            );

            if (!planId) {
                return null;
            }

            return findEnrollment(visibleEnrollments, planId, planYearData);
        },
        [enrollments, memberOf, planId, planTypes, planYearData],
    );

    useEffect(
        function guardHiddenBenefit() {
            if (!shouldLoadIndividualContext) {
                return;
            }

            if (isLoading || !planYearData || !planData) {
                return;
            }

            if (enrollment) {
                return;
            }

            const matchingPlanType = planTypes.find(function byPlanTypeId(row) {
                return row.planTypeId === planData.planTypeId;
            });

            if (canAccessPlanType(matchingPlanType, memberOf)) {
                return;
            }

            Toast.error(getText('msg_650d3be42592'));
            handleClose();
        },
        [
            enrollment,
            handleClose,
            isLoading,
            memberOf,
            planData,
            planTypes,
            planYearData,
            shouldLoadIndividualContext,
        ],
    );

    const groupedDetails = useMemo(
        function memoGrouped() {
            return buildGroupedDetails(planData, planYearId);
        },
        [planData, planYearId],
    );

    const isMedicalDentalVisionPlan = MED_DEN_VIS_PLAN_TYPES.includes(
        planData?.planTypeId,
    );
    const showOutOfNetwork = isMedicalDentalVisionPlan;
    const displayOutOfNetworkInformation =
        showOutOfNetwork && displayOutOfNetwork;

    const navigateToSSO = useCallback(
        async function openSSO() {
            void logEvent(
                LogEventType.UserLinkClicked,
                `Benefit plan page clicked: ${planData?.name || 'Unknown plan'}`,
                {
                    contextDataTypeId: EventDataTypeEnum.Benefits,
                    contextId: planLogId,
                    contextName: benefitPlanContextName,
                },
            );

            if (planData?.tenantSSOId == null) {
                if (ssoUrl) {
                    window.open(ssoUrl, '_blank');
                }
                return;
            }

            const pendingWindow = openPendingWindow();
            try {
                let ssoData;
                if (planData?.tenantSSOId != null) {
                    ssoData = await fetchClientSSOById(planData.tenantSSOId);
                    if (!hasText(ssoData?.url) && ssoData?.ssoId != null) {
                        ssoData = await fetchSystemSSOById(ssoData.ssoId);
                    }
                    const loginResponse = await loginClientSSO(ssoData);
                    const loginUrl = loginResponse?.login?.saml20?.url;
                    if (!isValidUrl(loginUrl)) {
                        showSSOLoginValidationWarning();
                        return;
                    }
                }

                if (!isValidUrl(loginUrl)) {
                    closePendingWindow(pendingWindow);
                    showSSOLoginValidationWarning();
                    return;
                }

                navigatePendingWindow(pendingWindow, loginUrl);
            } catch (err) {
                closePendingWindow(pendingWindow);
                console.error('Error attempting SSO', err);
                showSSOGenericError();
            }
        },
        [benefitPlanContextName, planData, ssoUrl, planLogId],
    );

    const handleBenefitPlanAssetOpen = useCallback(
        function onBenefitPlanAssetOpen(asset) {
            void logEvent(
                LogEventType.UserDocumentViewed,
                `Benefit plan asset clicked: ${asset?.name || 'Untitled asset'}`,
                {
                    subjectDataTypeId: EventDataTypeEnum.Asset,
                    subjectId: asset?.id ?? null,
                    subjectName: asset?.name || 'Untitled asset',
                    contextDataTypeId: EventDataTypeEnum.Benefits,
                    contextId: planLogId,
                    contextName: benefitPlanContextName,
                },
            );
        },
        [benefitPlanContextName, planLogId],
    );

    function handleOverviewLinkClick(linkLabel) {
        void logEvent(LogEventType.UserLinkClicked, `Visited ${linkLabel}`, {
            contextDataTypeId: EventDataTypeEnum.Benefits,
            contextId: planLogId,
            contextName: benefitPlanContextName,
        });
    }

    function hasActualContent(html) {
        if (!html) {
            return false;
        }
        const str = html.replace(/&nbsp;/gi, ' ').trim();
        const onlyEmpty =
            /^(?:\s|<br\s*\/?>|<\/?\s*[a-zA-Z0-9-]+\s*\/?>)*$/.test(str);
        return !onlyEmpty;
    }

    function formatPercentageValue(value) {
        if (value === null || value === undefined) {
            return '-';
        }
        const raw = String(value).trim();
        if (raw === '') {
            return '-';
        }
        if (/%\s*$/.test(raw)) {
            return raw.replace(/\s+$/, '');
        }
        return `${raw}%`;
    }

    function getInNetworkDisplay(detail) {
        if (detail.planDetailsValueTypeId === 1) {
            return formatCurrencyString(detail.inNetworkValue, {
                locale: LOCALE,
                currency: CURRENCY,
                emptyDisplay: '-',
            });
        }
        if (detail.planDetailsValueTypeId === 2) {
            return formatPercentageValue(detail.inNetworkValue);
        }
        return detail.inNetworkValue || '-';
    }

    function getOutNetworkDisplay(detail) {
        if (detail.planDetailsValueTypeId === 1) {
            return formatCurrencyString(detail.outNetworkValue, {
                locale: LOCALE,
                currency: CURRENCY,
                emptyDisplay: '-',
            });
        }
        if (detail.planDetailsValueTypeId === 2) {
            return formatPercentageValue(detail.outNetworkValue);
        }
        return detail.outNetworkValue || '-';
    }

    function getCategoryTextForYear(categoryId) {
        if (!planData?.planDetailCategories || planYearId == null) {
            return '';
        }
        const found = planData.planDetailCategories.find(function match(c) {
            return (
                String(c.planYearId) === String(planYearId) &&
                c.systemPlanDetailCategoryId === categoryId &&
                c.systemPlanDetailCategoryId !== HR_CONTACT_CATEGORY_ID
            );
        });
        const text = found?.text ?? '';
        return hasActualContent(text) ? text : '';
    }

    if (isLoading || !planData) {
        return <Spinner width={50} className="d-block mx-auto mt-5 pt-5" />;
    }

    const coverageStart = formatCoverageDate(enrollment?.startDate);
    const coverageEnd = formatCoverageDate(enrollment?.endDate);

    const overviewItems = [
        buildOverviewItem(getText('msg_45061ce8ef30'), planData.planType?.name),
        buildOverviewItem(
            getText('msg_ec0632cbbffa'),
            planData.overrideName || planData.name,
        ),
        buildOverviewItem(
            getText('msg_1a2b3c4d5e6f'),
            planData.networkType?.name,
        ),
        buildOverviewItem(getText('msg_b1d16c242a46'), planData.networkName),
        buildOverviewItem(getText('msg_bb9cf1418089'), planData.policy),
        buildOverviewItem(
            getText('msg_26a649a2a3e9'),
            planData.providerList ? getText('msg_4d5e6f708192') : '',
            {
                kind: 'link',
                href: planData.providerList,
                onClick: function handleProviderListClick() {
                    handleOverviewLinkClick('Provider URL');
                },
            },
        ),
        buildOverviewItem(getText('msg_91b0ba24657f'), planData.vendor?.name),
        buildOverviewItem(getText('msg_72ff10feb632'), planData.url, {
            kind: 'link',
            href: planData.url,
            onClick: function handleCarrierWebsiteClick() {
                handleOverviewLinkClick('Carrier URL');
            },
        }),
        buildOverviewItem(
            getText('msg_2b3c4d5e6f70'),
            planData.tenantSSOId != null ? getText('msg_3c4d5e6f7081') : '',
            {
                kind: 'button',
                onClick:
                    planData.tenantSSOId != null ? navigateToSSO : undefined,
                ariaLabel: getText('msg_6f708192a3b4', {
                    carrierName:
                        planData.vendor?.name || getText('msg_91b0ba24657f'),
                }),
            },
        ),
        buildOverviewItem(
            getText('msg_5e6f708192a3'),
            planData.phoneNumber ? formatPhoneNumber(planData.phoneNumber) : '',
            {
                kind: 'link',
                href: planData.phoneNumber
                    ? `tel:${planData.phoneNumber.replace(/[^\d+]/g, '')}`
                    : '',
                ariaLabel: planData.phoneNumber
                    ? getText('msg_708192a3b4c5', {
                          phoneNumber: formatPhoneNumber(planData.phoneNumber),
                      })
                    : undefined,
            },
        ),
        buildOverviewItem(getText('msg_96791b6925a4'), coverageStart),
        buildOverviewItem(getText('msg_a40f380753b0'), coverageEnd),
        ...buildEnrollmentOverviewItems({
            enrollment,
            displayEmployeeCosts,
            displayEmployerCosts,
            formatMoney,
            getText,
        }),
    ];

    const contactHtml = findCategoryText(
        planData,
        planYearId,
        HR_CONTACT_CATEGORY_ID,
    );

    return (
        <div className="page-content">
            <div className={`ing-card ${styles.card}`}>
                <div className="p-4 p-md-5">
                    <header className="d-flex align-items-center justify-content-between gap-3 mb-4">
                        <h1 className="ing-text-heading3 mb-0">
                            {planData.overrideName || planData.name}
                        </h1>
                        <button
                            type="button"
                            className="btn btn-link link-secondary text-decoration-none d-inline-flex align-items-center gap-1"
                            onClick={handleClose}
                        >
                            <i className="bi bi-x-lg" aria-hidden />
                            {getText('msg_bbfa773e5a63')}
                        </button>
                    </header>

                    <PlanDetailHero plan={planData} />

                    {hasActualContent(planData?.headerDisplay) && (
                        <RichTextContent content={planData.headerDisplay} />
                    )}

                    <section className="mt-5">
                        <h2 className="ing-text-heading4 mb-3">
                            {getText('msg_6dd2dc9d4b01')}
                        </h2>
                        <PlanOverviewGrid items={overviewItems} />
                    </section>

                    <div className="w-100 mt-5">
                        {groupedDetails?.length > 0 && (
                            <div className={styles.rowStyle}>
                                <div
                                    className={`${styles.headerCellStyle} ${
                                        isMobile
                                            ? styles.headerCellMobile
                                            : styles.headerCellDesktop
                                    }`}
                                >
                                    {getText('msg_49f6b730623e')}
                                </div>
                                {showOutOfNetwork && (
                                    <div
                                        className={`${styles.headerCellStyle} ${styles.headerAlignCenter} ${
                                            isMobile
                                                ? styles.networkHeaderMobile
                                                : styles.networkHeaderDesktop
                                        }`}
                                    >
                                        {groupedDetails.length > 0
                                            ? getText('msg_e6e125cba048')
                                            : ''}
                                    </div>
                                )}
                                {showOutOfNetwork &&
                                    displayOutOfNetworkInformation && (
                                        <div
                                            className={`${styles.headerCellStyle} ${styles.headerAlignCenter} ${
                                                isMobile
                                                    ? styles.networkHeaderMobile
                                                    : styles.networkHeaderDesktop
                                            }`}
                                        >
                                            {groupedDetails.length > 0
                                                ? getText('msg_7a43cdd08459')
                                                : ''}
                                        </div>
                                    )}
                            </div>
                        )}

                        {!isMedicalDentalVisionPlan &&
                            hasActualContent(planDetailCategoryContent) && (
                                <div className="mb-4">
                                    <RichTextContent
                                        content={planDetailCategoryContent}
                                    />
                                </div>
                            )}

                        {groupedDetails?.length > 0 && (
                            <>
                                {groupedDetails.map(
                                    function renderGroup(group, index) {
                                        const categoryText =
                                            getCategoryTextForYear(
                                                group.categoryId,
                                            );
                                        return (
                                            <div
                                                key={group.categoryId}
                                                className={`${styles.rowStyle} ${
                                                    index % 2 === 0
                                                        ? styles.rowAlternate
                                                        : ''
                                                }`}
                                            >
                                                <div
                                                    className={`${styles.firstColumnStyle} ${styles.firstColumnWhite}`}
                                                >
                                                    <div>
                                                        {group.categoryName}
                                                    </div>
                                                    {categoryText && (
                                                        <div
                                                            className={
                                                                styles.categoryCustomText
                                                            }
                                                        >
                                                            <RichTextContent
                                                                content={
                                                                    categoryText
                                                                }
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                <div
                                                    className={
                                                        styles.contentColumnStyle
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.itemWrapper
                                                        }
                                                    >
                                                        {group.details.map(
                                                            function renderDetail(
                                                                detail,
                                                                detailIndex,
                                                            ) {
                                                                return (
                                                                    <div
                                                                        key={
                                                                            detailIndex
                                                                        }
                                                                        className={
                                                                            styles.itemStackStyle
                                                                        }
                                                                    >
                                                                        <div
                                                                            className={
                                                                                styles.itemLabelStyle
                                                                            }
                                                                        >
                                                                            {detail.name ||
                                                                                detail
                                                                                    .systemPlanDetail
                                                                                    ?.name}
                                                                        </div>
                                                                        <div
                                                                            className={
                                                                                styles.itemValueStyle
                                                                            }
                                                                        >
                                                                            {getInNetworkDisplay(
                                                                                detail,
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            },
                                                        )}
                                                    </div>
                                                </div>
                                                {showOutOfNetwork &&
                                                    displayOutOfNetworkInformation && (
                                                        <div
                                                            className={
                                                                styles.contentColumnStyle
                                                            }
                                                        >
                                                            <div
                                                                className={
                                                                    styles.itemWrapper
                                                                }
                                                            >
                                                                {group.details.map(
                                                                    function renderOutDetail(
                                                                        detail,
                                                                        detailIndex,
                                                                    ) {
                                                                        return (
                                                                            <div
                                                                                key={
                                                                                    detailIndex
                                                                                }
                                                                                className={
                                                                                    styles.itemStackStyle
                                                                                }
                                                                            >
                                                                                <div
                                                                                    className={
                                                                                        styles.itemLabelStyle
                                                                                    }
                                                                                >
                                                                                    {detail.name ||
                                                                                        detail
                                                                                            .systemPlanDetail
                                                                                            ?.name}
                                                                                </div>
                                                                                <div
                                                                                    className={
                                                                                        styles.itemValueStyle
                                                                                    }
                                                                                >
                                                                                    {getOutNetworkDisplay(
                                                                                        detail,
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    },
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                            </div>
                                        );
                                    },
                                )}
                            </>
                        )}
                    </div>

                    {planData.planAssets?.length > 0 && (
                        <section className="mt-5">
                            <h2 className="ing-text-heading4 mb-3">
                                {getText('msg_20e338624cee')}
                            </h2>
                            <AssetsRow
                                assets={planData.planAssets}
                                assetDetailsMap={assetDetailsMap}
                                onAssetOpen={handleBenefitPlanAssetOpen}
                                planId={planLogId}
                                contextName={benefitPlanContextName}
                            />
                        </section>
                    )}
                </div>
            </div>

            {isFromViewBenefits && (
                <div className={styles.viewBenefitsBannerWrapper}>
                    <div className={styles.viewBenefitsBanner}>
                        <i
                            className={`bi bi-info-circle ${styles.viewBenefitsBannerIcon}`}
                        />
                        <span className={styles.viewBenefitsBannerText}>
                            <strong>{getText('msg_8192a3b4c5d6')}</strong>{' '}
                            {getText('msg_92a3b4c5d6e7')}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}
